import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import SubscriptionLunaKeysTable from "../components/billing/SubscriptionLunaKeysTable";
import { Shimmer } from "../components/MotionPrimitives";
import { useAuth } from "../contexts/AuthContext";
import useRouteTab from "../hooks/useRouteTab";
import apiService from "../services/api";
import { buildDetailNavigationState } from "../utils/navigation";
import { getCustomerPortal } from "../utils/stripe";

const AMAZON_MANAGE_SUBSCRIPTION_URL =
  "https://www.amazon.com/gp/mas/your-account/myapps";
const BILLING_TAB_IDS = ["invoices", "subscriptions"];

const getPaymentProviderDisplay = (provider) => {
  if (provider === "amazon") {
    return {
      label: "Amazon",
      title: "Amazon Appstore Billing",
      icon: "fab fa-amazon",
      className: "bg-gradient-to-r from-orange-400 to-amber-500 text-gray-950",
    };
  }

  if (provider === "apple") {
    return {
      label: "Apple",
      title: "Apple In-App Purchase",
      icon: "fab fa-apple",
      className: "bg-gradient-to-r from-gray-700 to-gray-900 text-white",
    };
  }

  return {
    label: "Stripe",
    title: "Stripe",
    icon: "fab fa-stripe",
    className: "bg-gradient-to-r from-purple-500 to-indigo-500 text-white",
  };
};

const INVOICE_STATUS_LABELS = {
  paid: "Paid",
  trial: "Active Trial",
  converted_trial: "Converted Trial",
  expired_trial: "Expired Trial",
  refunded: "Refunded",
  sent: "Sent",
  viewed: "Viewed",
  overdue: "Overdue",
  draft: "Draft",
  cancelled: "Cancelled",
};

const INVOICE_STATUS_ORDER = {
  paid: 10,
  trial: 20,
  overdue: 30,
  refunded: 40,
  expired_trial: 50,
  converted_trial: 60,
  sent: 70,
  viewed: 80,
  draft: 90,
  cancelled: 100,
};

const formatInvoiceStatus = (status) => {
  if (!status) return "Unknown";

  return (
    INVOICE_STATUS_LABELS[status] ||
    String(status)
      .split(/[_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
};

const getStatusFilterLabel = (selectedStatuses) => {
  if (!selectedStatuses.length) return "All Status";
  if (selectedStatuses.length === 1) {
    return formatInvoiceStatus(selectedStatuses[0]);
  }

  return `${selectedStatuses.length} Statuses`;
};

const getInvoiceStatusOptions = (statusFacets = []) =>
  statusFacets
    .map(({ value, count }) => ({
      count,
      label: formatInvoiceStatus(value),
      value,
    }))
    .filter((option) => option.value)
    .sort((a, b) => {
      const aOrder = INVOICE_STATUS_ORDER[a.value] ?? 999;
      const bOrder = INVOICE_STATUS_ORDER[b.value] ?? 999;

      if (aOrder !== bOrder) return aOrder - bOrder;

      return a.label.localeCompare(b.label);
    });

const InvoiceStatusDropdown = ({
  isOpen,
  onClear,
  onToggle,
  onToggleOpen,
  options,
  selectedStatuses,
  wrapperRef,
}) => {
  const selectedSet = new Set(selectedStatuses);
  const label = getStatusFilterLabel(selectedStatuses);

  return (
    <div className="relative w-full sm:w-auto" ref={wrapperRef}>
      <button
        aria-expanded={isOpen}
        className="input w-full sm:w-52 flex items-center justify-between gap-3 text-left"
        type="button"
        onClick={onToggleOpen}
      >
        <span className="truncate">{label}</span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} text-xs text-gray-400`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-full sm:w-64 rounded-lg border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 z-50 dark:border-gray-700 dark:bg-gray-900">
          <div className="p-2">
            <button
              className="w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              type="button"
              onClick={onClear}
            >
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 inline-flex items-center justify-center">
                  {!selectedStatuses.length && (
                    <i className="fas fa-check text-xs text-primary-500" />
                  )}
                </span>
                All Status
              </span>
            </button>

            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

            {options.length > 0 ? (
              options.map((option) => (
                <label
                  className="w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer dark:text-gray-200 dark:hover:bg-gray-800"
                  key={option.value}
                >
                  <span className="inline-flex items-center gap-2">
                    <input
                      checked={selectedSet.has(option.value)}
                      className="rounded border-gray-300 dark:border-gray-600"
                      type="checkbox"
                      onChange={() => onToggle(option.value)}
                    />
                    {option.label}
                  </span>
                  <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
                    {option.count.toLocaleString()}
                  </span>
                </label>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No invoice statuses found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapInvoiceForList = (invoice) => ({
  id: invoice.prettyInvoiceId || invoice.invoiceNumber || invoice.id,
  customer: invoice.customer || "Unknown Customer",
  customerEmail: invoice.customerEmail || "",
  amount: invoice.total || invoice.amount || 0,
  status: invoice.status,
  dueDate: invoice.dueDate
    ? new Date(invoice.dueDate).toISOString().split("T")[0]
    : "",
  issueDate: invoice.issueDate
    ? new Date(invoice.issueDate).toISOString().split("T")[0]
    : new Date(invoice.createdAt).toISOString().split("T")[0],
  items: invoice.items?.length || 1,
  transactionId: invoice.transactionId,
  trialEnd: invoice.trialEnd,
  trialStatus: invoice.trialStatus,
  paymentProvider: invoice.paymentProvider || "stripe",
  paymentProviderLabel: invoice.paymentProviderLabel || "Stripe",
  paymentMethodLabel: invoice.paymentMethodLabel || "Credit Card",
});

const Invoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [dateRange, setDateRange] = useState("all");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("issueDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoiceStatusFacets, setInvoiceStatusFacets] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({
    activeTrials: 0,
    overdueAmount: 0,
    totalInvoices: 0,
    totalRevenue: 0,
  });
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showPageInput, setShowPageInput] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const [activeBillingTab, setActiveBillingTab] = useRouteTab({
    defaultTab: "invoices",
    validTabs: BILLING_TAB_IDS,
  });
  const [activeBillingProviders, setActiveBillingProviders] = useState([]);
  const statusDropdownRef = useRef(null);
  const isModuleReseller =
    user?.type === "Reseller" && user?.resellerType === "module";
  const canViewSubscriptionLunaKeys =
    user?.role === "user" &&
    (user?.type === "Premium" || user?.type === "Reseller");
  const historicalPaymentProviders = new Set(
    invoices.map((invoice) => invoice.paymentProvider).filter(Boolean)
  );
  const activePaymentProviders = new Set(activeBillingProviders);
  const billingPaymentProviders =
    activeBillingProviders.length > 0
      ? activePaymentProviders
      : historicalPaymentProviders;
  const usesAmazonBilling =
    billingPaymentProviders.has("amazon") &&
    !billingPaymentProviders.has("stripe");
  const selectedStatusKey = selectedStatuses.join("|");
  const statusOptions = useMemo(
    () => getInvoiceStatusOptions(invoiceStatusFacets),
    [invoiceStatusFacets]
  );

  const clearStatusFilter = () => {
    setSelectedStatuses([]);
  };

  const toggleStatusFilter = (status) => {
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status]
    );
  };

  const fetchActiveBillingProviders = useCallback(async () => {
    if (!canViewSubscriptionLunaKeys || isModuleReseller) {
      setActiveBillingProviders([]);

      return;
    }

    try {
      const response = await apiService.getSubscriptionLunaKeys({
        page: 1,
        limit: 100,
        status: "active",
      });
      const providers = [
        ...new Set(
          (response.items || [])
            .map(
              (subscription) =>
                subscription.provider || subscription.paymentProvider
            )
            .filter(Boolean)
        ),
      ];

      setActiveBillingProviders(providers);
    } catch (error) {
      console.error("Error fetching active billing providers:", error);
      setActiveBillingProviders([]);
    }
  }, [canViewSubscriptionLunaKeys, isModuleReseller]);

  const handleManageSubscription = useCallback(() => {
    if (usesAmazonBilling) {
      window.open(
        AMAZON_MANAGE_SUBSCRIPTION_URL,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    const email = user?.email || "";

    window.open(
      getCustomerPortal(encodeURIComponent(email)),
      "_blank",
      "noopener,noreferrer"
    );
  }, [user?.email, usesAmazonBilling]);

  const manageSubscriptionLabel = usesAmazonBilling
    ? "Manage in Amazon"
    : "Manage Subscription";
  const manageSubscriptionIcon = usesAmazonBilling
    ? "fab fa-amazon"
    : "fas fa-credit-card";

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        includeMeta: "true",
        page: currentPage,
        limit: itemsPerPage,
        sort: "created_at",
        order: "DESC",
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (selectedStatusKey) {
        params.status = selectedStatusKey;
      }

      if (dateRange !== "all") {
        params.dateRange = dateRange;
      }

      const response = await apiService.getInvoices(params);

      if (response.pagination) {
        setTotalPages(response.pagination.pages || 1);
        setTotalInvoices(response.pagination.total || 0);
      }

      const transformedInvoices = response.invoices.map(mapInvoiceForList);

      setInvoices(transformedInvoices);
      setInvoiceStatusFacets(response.facets?.statuses || []);
      setInvoiceSummary((current) => ({
        ...current,
        ...(response.summary || {}),
      }));
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error(error.message || "Failed to load invoices");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateRange, itemsPerPage, searchTerm, selectedStatusKey]);

  useEffect(() => {
    fetchActiveBillingProviders();
  }, [fetchActiveBillingProviders]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (!showStatusDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showStatusDropdown]);

  useEffect(() => {
    if (statusOptions.length === 0 || selectedStatuses.length === 0) return;

    const availableStatuses = new Set(
      statusOptions.map((option) => option.value)
    );
    const nextStatuses = selectedStatuses.filter((status) =>
      availableStatuses.has(status)
    );

    if (nextStatuses.length !== selectedStatuses.length) {
      setSelectedStatuses(nextStatuses);
    }
  }, [statusOptions, selectedStatuses]);

  useEffect(() => {
    if (!canViewSubscriptionLunaKeys && activeBillingTab === "subscriptions") {
      setActiveBillingTab("invoices", { replace: true });
    }
  }, [activeBillingTab, canViewSubscriptionLunaKeys, setActiveBillingTab]);

  const fetchCurrentCustomerSubscriptions = useCallback(
    (params) => apiService.getSubscriptionLunaKeys(params),
    []
  );

  const cancelCurrentCustomerSubscription = useCallback(
    (redeemCodeId, options) =>
      apiService.cancelSubscriptionLunaKey(redeemCodeId, options),
    []
  );

  const handleSubscriptionCancelled = async () => {
    await fetchActiveBillingProviders();
    await fetchInvoices();
  };

  const handleViewInvoice = (transactionId) => {
    navigate(`/invoices/${transactionId}`, {
      state: buildDetailNavigationState(location),
    });
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(
        field === "issueDate" || field === "dueDate" ? "desc" : "asc"
      );
    }
  };

  // Sort the current server-paginated page for local column toggles.
  const sortedInvoices = [...invoices].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === "issueDate" || sortField === "dueDate") {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (!aValue) aValue = "";
    if (!bValue) bValue = "";

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    }

    return 0;
  });

  const paginatedInvoices = sortedInvoices;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatusKey, dateRange]);

  // Change page size, reset to page 1, and refetch with the new limit
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInvoices(paginatedInvoices.map((i) => i.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleBulkAction = (action) => {
    if (selectedInvoices.length === 0) return;

    if (action === "delete") {
      if (window.confirm(`Delete ${selectedInvoices.length} invoice(s)?`)) {
        setInvoices((prev) =>
          prev.filter((i) => !selectedInvoices.includes(i.id))
        );
        toast.success(`${selectedInvoices.length} invoice(s) deleted`);
        setSelectedInvoices([]);
      }
    } else if (action === "send") {
      toast.success(`${selectedInvoices.length} invoice(s) sent`);
      setSelectedInvoices([]);
    } else if (action === "download") {
      toast.success(`Downloading ${selectedInvoices.length} invoice(s)...`);
      setSelectedInvoices([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "badge-success";
      case "trial":
        return "badge-info";
      case "converted_trial":
        return "badge-primary";
      case "expired_trial":
        return "badge-secondary";
      case "sent":
        return "badge-warning";
      case "viewed":
        return "badge-info";
      case "overdue":
        return "badge-danger";
      case "draft":
        return "badge-secondary";
      case "cancelled":
        return "badge-secondary";
      case "refunded":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  };

  // Customer-specific UI
  if (user?.role === "user") {
    return (
      <div className="space-y-4 sm:space-y-6" style={{ overflow: "visible" }}>
        {canViewSubscriptionLunaKeys ? (
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2 overflow-x-auto">
                {[
                  {
                    id: "invoices",
                    label: "Invoices",
                    icon: "fas fa-file-invoice",
                  },
                  {
                    id: "subscriptions",
                    label: "Subscriptions",
                    icon: "fas fa-key",
                  },
                ].map((tab) => (
                  <button
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeBillingTab === tab.id
                        ? "border-primary-600 text-primary-600 dark:text-primary-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveBillingTab(tab.id)}
                  >
                    <i className={tab.icon} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
              {!isModuleReseller && (
                <button
                  className="btn btn-primary w-full justify-center sm:w-auto sm:shrink-0"
                  onClick={handleManageSubscription}
                >
                  <i className={`${manageSubscriptionIcon} mr-2`} />
                  <span>{manageSubscriptionLabel}</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            {!isModuleReseller && (
              <button
                className="btn btn-primary w-full justify-center sm:w-auto"
                onClick={handleManageSubscription}
              >
                <i className={`${manageSubscriptionIcon} mr-2`} />
                <span>{manageSubscriptionLabel}</span>
              </button>
            )}
          </div>
        )}

        {canViewSubscriptionLunaKeys &&
          activeBillingTab === "subscriptions" && (
            <SubscriptionLunaKeysTable
              cancelSubscriptionLunaKey={cancelCurrentCustomerSubscription}
              fetchSubscriptionLunaKeys={fetchCurrentCustomerSubscriptions}
              isModuleReseller={isModuleReseller}
              onCancelled={handleSubscriptionCancelled}
            />
          )}

        {/* Invoices Section */}
        {(activeBillingTab === "invoices" || !canViewSubscriptionLunaKeys) && (
          <div
            className="bg-white rounded-xl shadow-md border border-gray-100 dark:bg-gray-800/50 dark:border-gray-700/50 dark:backdrop-blur-sm"
            style={{ overflow: "visible" }}
          >
            <div
              className="p-4 border-b border-gray-200 dark:border-gray-700"
              style={{ overflow: "visible" }}
            >
              <div className="flex flex-col gap-3 sm:gap-4">
                <h2 className="text-lg sm:text-xl font-semibold">
                  My Invoices
                </h2>

                {/* Compact Filters Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="w-full sm:w-64">
                    <div className="relative">
                      <input
                        className="input pl-10 w-full text-sm"
                        placeholder="Search invoices..."
                        style={{ height: "45px" }}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                    </div>
                  </div>
                  <InvoiceStatusDropdown
                    isOpen={showStatusDropdown}
                    options={statusOptions}
                    selectedStatuses={selectedStatuses}
                    wrapperRef={statusDropdownRef}
                    onClear={clearStatusFilter}
                    onToggle={toggleStatusFilter}
                    onToggleOpen={() =>
                      setShowStatusDropdown((current) => !current)
                    }
                  />
                  <select
                    className="input h-[45px] py-0 pr-9 text-sm leading-5"
                    style={{ width: "140px" }}
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="7days">Last 7 Days</option>
                    <option value="30days">Last 30 Days</option>
                    <option value="90days">Last 90 Days</option>
                  </select>
                  <select
                    className="input h-[45px] py-0 pr-9 text-sm leading-5"
                    style={{ width: "140px" }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Invoices List */}
            <div
              className="overflow-x-auto"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <table className="w-full" style={{ minWidth: "1000px" }}>
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedInvoices.map((invoice, index) => (
                    <tr
                      className={`${index % 2 === 0 ? "table-row-striped-even" : "table-row-striped-odd"} table-row-hover`}
                      key={invoice.id}
                    >
                      <td className="px-6 py-4">
                        <button
                          className="text-primary-600 hover:text-primary-700 font-medium"
                          onClick={() =>
                            handleViewInvoice(invoice.transactionId)
                          }
                        >
                          #{invoice.id}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm">{invoice.issueDate}</td>
                      <td className="px-6 py-4">
                        <span className="font-medium">
                          ${invoice.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`badge ${getStatusColor(invoice.status)}`}
                        >
                          {formatInvoiceStatus(invoice.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`w-14 h-8 rounded flex items-center justify-center ${getPaymentProviderDisplay(invoice.paymentProvider).className}`}
                          title={
                            invoice.paymentProviderLabel ||
                            getPaymentProviderDisplay(invoice.paymentProvider)
                              .title
                          }
                        >
                          <i
                            className={`${getPaymentProviderDisplay(invoice.paymentProvider).icon} text-lg`}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            className="btn btn-ghost btn-sm"
                            title="View Invoice"
                            onClick={() =>
                              handleViewInvoice(invoice.transactionId)
                            }
                          >
                            <i className="fas fa-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {paginatedInvoices.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <i className="fas fa-file-invoice text-4xl mb-3" />
                  <p>No invoices found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalInvoices > 0 && totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
                  {`Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalInvoices)} to ${Math.min(currentPage * itemsPerPage, totalInvoices)} of ${totalInvoices} entries`}
                </p>
                <div className="flex gap-2 justify-center items-center">
                  <button
                    className="px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 px-1">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Admin UI
  const stats =
    user?.role === "admin"
      ? [
          {
            label: "Total Revenue",
            value: `$${Number(invoiceSummary.totalRevenue || 0).toLocaleString()}`,
            change: "+18%",
            icon: "fas fa-dollar-sign",
            color: "bg-green-500",
          },
          {
            label: "Active Trials",
            value: Number(invoiceSummary.activeTrials || 0).toLocaleString(),
            change: "-5%",
            icon: "fas fa-flask",
            color: "bg-yellow-500",
          },
          {
            label: "Overdue",
            value: `$${Number(invoiceSummary.overdueAmount || 0).toLocaleString()}`,
            change: "+2%",
            icon: "fas fa-exclamation-triangle",
            color: "bg-red-500",
          },
          {
            label: "Total Invoices",
            value: Number(invoiceSummary.totalInvoices || 0).toLocaleString(),
            change: "+12%",
            icon: "fas fa-file-invoice",
            color: "bg-blue-500",
          },
        ]
      : [];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Action Buttons */}
      {user?.role === "admin" ? (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button className="btn btn-ghost w-full sm:w-auto">
            <i className="fas fa-download mr-2" />
            <span>Export</span>
          </button>
          <button
            className="btn btn-primary w-full sm:w-auto"
            onClick={() => setShowAddInvoiceModal(true)}
          >
            <i className="fas fa-plus mr-2" />
            <span>Add Invoice</span>
          </button>
        </div>
      ) : (
        user?.role === "user" &&
        !isModuleReseller && (
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              className="btn btn-primary w-full sm:w-auto"
              onClick={handleManageSubscription}
            >
              <i className={`${manageSubscriptionIcon} mr-2`} />
              <span>{manageSubscriptionLabel}</span>
            </button>
          </div>
        )
      )}

      {/* Stats - Only show for admin users */}
      {user?.role === "admin" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((stat, index) => (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50"
              initial={{ opacity: 0, y: 20 }}
              key={index}
              style={{
                padding: "16px",
                overflow: "visible",
              }}
              transition={{ delay: index * 0.1 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  minHeight: "40px",
                }}
              >
                <div
                  className={`${stat.color} rounded-lg`}
                  style={{
                    width: "40px",
                    height: "40px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={`${stat.icon} text-white`}
                    style={{
                      fontSize: "16px",
                      display: "block",
                    }}
                  />
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    className="font-bold text-gray-900 dark:text-white"
                    style={{
                      fontSize: "20px",
                      lineHeight: "1.2",
                      display: "block",
                      visibility: "visible",
                      opacity: 1,
                      color: "inherit",
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-gray-600 dark:text-gray-300"
                    style={{
                      fontSize: "12px",
                      marginTop: "2px",
                      display: "block",
                      visibility: "visible",
                      opacity: 1,
                      color: "inherit",
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 sm:p-6 shadow-md overflow-visible">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="w-full">
            <div className="relative">
              <input
                className="input pl-10 w-full"
                placeholder="Search invoices..."
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <InvoiceStatusDropdown
              isOpen={showStatusDropdown}
              options={statusOptions}
              selectedStatuses={selectedStatuses}
              wrapperRef={statusDropdownRef}
              onClear={clearStatusFilter}
              onToggle={toggleStatusFilter}
              onToggleOpen={() => setShowStatusDropdown((current) => !current)}
            />
            <select
              className="input w-full sm:w-auto"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
            <select
              className="input w-full sm:w-auto"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          {selectedInvoices.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                className="btn btn-ghost text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full sm:w-auto"
                onClick={() => handleBulkAction("delete")}
              >
                <i className="fas fa-trash mr-2" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50">
        <div
          className="overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <table className="w-full" style={{ minWidth: "1320px" }}>
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="table-header whitespace-nowrap">
                  <input
                    checked={
                      selectedInvoices.length === sortedInvoices.length &&
                      sortedInvoices.length > 0
                    }
                    className="rounded border-gray-300 dark:border-gray-600"
                    type="checkbox"
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("id")}
                >
                  <div className="flex items-center gap-1">
                    Invoice
                    {sortField === "id" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("customer")}
                >
                  <div className="flex items-center gap-1">
                    Customer
                    {sortField === "customer" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("amount")}
                >
                  <div className="flex items-center gap-1">
                    Amount
                    {sortField === "amount" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("paymentProviderLabel")}
                >
                  <div className="flex items-center gap-1">
                    Gateway
                    {sortField === "paymentProviderLabel" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === "status" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header text-left whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("issueDate")}
                >
                  <div className="flex items-center gap-1">
                    Issue Date
                    {sortField === "issueDate" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th className="table-header text-left whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr
                    className="border-b border-gray-100 dark:border-gray-700"
                    key={idx}
                  >
                    {Array.from({ length: 8 }).map((__, cellIdx) => (
                      <td className="px-3 py-3" key={cellIdx}>
                        <Shimmer className="h-3.5 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginatedInvoices.length === 0 ? (
                <tr>
                  <td className="text-center py-8 text-gray-500" colSpan="8">
                    No invoices found
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice, index) => (
                  <tr
                    className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer ${
                      index % 2 === 0
                        ? "table-row-striped-even"
                        : "table-row-striped-odd"
                    } table-row-hover`}
                    key={invoice.id}
                    onClick={() => handleViewInvoice(invoice.transactionId)}
                  >
                    <td
                      className="table-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        checked={selectedInvoices.includes(invoice.id)}
                        className="rounded border-gray-300 dark:border-gray-600"
                        type="checkbox"
                        onChange={() => handleSelectInvoice(invoice.id)}
                      />
                    </td>
                    <td className="table-cell">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {invoice.id}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {invoice.items} items
                        </p>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {invoice.customer}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {invoice.customerEmail}
                        </p>
                      </div>
                    </td>
                    <td className="table-cell">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        ${invoice.amount.toLocaleString()}
                      </p>
                    </td>
                    <td className="table-cell">
                      <div className="inline-flex items-center gap-2 min-w-0">
                        <span
                          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${getPaymentProviderDisplay(invoice.paymentProvider).className}`}
                          title={
                            invoice.paymentProviderLabel ||
                            getPaymentProviderDisplay(invoice.paymentProvider)
                              .title
                          }
                        >
                          <i
                            className={`${getPaymentProviderDisplay(invoice.paymentProvider).icon} text-base`}
                          />
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {invoice.paymentProviderLabel ||
                              getPaymentProviderDisplay(invoice.paymentProvider)
                                .label}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {invoice.paymentMethodLabel}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span
                        className={`badge ${getStatusColor(invoice.status)}`}
                      >
                        {formatInvoiceStatus(invoice.status)}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500 dark:text-gray-400">
                      {new Date(invoice.issueDate).toLocaleDateString()}
                    </td>
                    <td
                      className="table-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          className="p-2 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                          title="View Invoice"
                          onClick={() =>
                            handleViewInvoice(invoice.transactionId)
                          }
                        >
                          <i className="fas fa-eye" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalInvoices > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
              {totalInvoices === 0
                ? "No entries to display"
                : totalInvoices === 1
                  ? "Showing 1 entry"
                  : `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalInvoices)} to ${Math.min(currentPage * itemsPerPage, totalInvoices)} of ${totalInvoices} entries`}
            </p>
            <div className="flex gap-1 sm:gap-2 justify-center items-center">
              <button
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {/* First page */}
              {totalPages > 0 && (
                <button
                  className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-lg transition-all duration-200 hover:shadow-md ${
                    currentPage === 1
                      ? "bg-primary-600 text-white hover:bg-primary-700"
                      : "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </button>
              )}

              {/* Left ellipsis with page input */}
              {currentPage > 3 && totalPages > 5 && (
                <div className="relative">
                  {showPageInput ? (
                    <input
                      autoFocus
                      className="w-12 sm:w-16 px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      max={totalPages}
                      min={1}
                      placeholder="..."
                      type="number"
                      value={pageInputValue}
                      onBlur={() => {
                        setShowPageInput(false);
                        setPageInputValue("");
                      }}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const page = parseInt(pageInputValue, 10);

                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                          }
                          setShowPageInput(false);
                          setPageInputValue("");
                        }
                      }}
                    />
                  ) : (
                    <button
                      className="px-2 py-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                      onClick={() => setShowPageInput(true)}
                    >
                      ...
                    </button>
                  )}
                </div>
              )}

              {/* Middle pages */}
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;

                // Skip first and last page (already rendered)
                if (page === 1 || page === totalPages) return null;

                // Show pages around current page
                if (page >= currentPage - 1 && page <= currentPage + 1) {
                  return (
                    <button
                      className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-lg transition-all duration-200 hover:shadow-md ${
                        page === currentPage
                          ? "bg-primary-600 text-white hover:bg-primary-700"
                          : "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                      key={page}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                }

                return null;
              })}

              {/* Right ellipsis with page input */}
              {currentPage < totalPages - 2 && totalPages > 5 && (
                <div className="relative">
                  {showPageInput ? (
                    <input
                      autoFocus
                      className="w-12 sm:w-16 px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      max={totalPages}
                      min={1}
                      placeholder="..."
                      type="number"
                      value={pageInputValue}
                      onBlur={() => {
                        setShowPageInput(false);
                        setPageInputValue("");
                      }}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const page = parseInt(pageInputValue, 10);

                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                          }
                          setShowPageInput(false);
                          setPageInputValue("");
                        }
                      }}
                    />
                  ) : (
                    <button
                      className="px-2 py-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                      onClick={() => setShowPageInput(true)}
                    >
                      ...
                    </button>
                  )}
                </div>
              )}

              {/* Last page */}
              {totalPages > 1 && (
                <button
                  className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-lg transition-all duration-200 hover:shadow-md ${
                    currentPage === totalPages
                      ? "bg-primary-600 text-white hover:bg-primary-700"
                      : "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </button>
              )}

              <button
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Invoice Modal */}
      {showAddInvoiceModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddInvoiceModal(false);
            }
          }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            exit={{ opacity: 0, scale: 0.9 }}
            initial={{ opacity: 0, scale: 0.9 }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Create New Invoice
              </h2>
              <button
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                onClick={() => setShowAddInvoiceModal(false)}
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Invoice Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Handle form submission
                toast.success("Invoice created successfully!");
                setShowAddInvoiceModal(false);
              }}
            >
              <div className="space-y-4">
                {/* Customer Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Customer Name *
                    </label>
                    <input
                      required
                      className="input w-full"
                      placeholder="Enter customer name"
                      type="text"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Customer Email *
                    </label>
                    <input
                      required
                      className="input w-full"
                      placeholder="customer@example.com"
                      type="email"
                    />
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invoice Number *
                    </label>
                    <input
                      required
                      className="input w-full"
                      placeholder="INV-001"
                      type="text"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status *
                    </label>
                    <select required className="input w-full">
                      <option value="">Select status</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                      <option value="trial">Trial</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Issue Date *
                    </label>
                    <input required className="input w-full" type="date" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Due Date *
                    </label>
                    <input required className="input w-full" type="date" />
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      required
                      className="input w-full pl-8"
                      min="0"
                      placeholder="0.00"
                      step="0.01"
                      type="number"
                    />
                  </div>
                </div>

                {/* Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number of Items
                  </label>
                  <input
                    className="input w-full"
                    defaultValue="1"
                    min="1"
                    type="number"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes
                  </label>
                  <textarea
                    className="input w-full"
                    placeholder="Additional notes or comments..."
                    rows="3"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  type="button"
                  onClick={() => setShowAddInvoiceModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold hover:from-purple-700 hover:to-purple-800 transition-all"
                  type="submit"
                >
                  <i className="fas fa-save mr-2" />
                  Create Invoice
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
